import type { CSSProperties } from 'react';
import { parseJson } from '@/lib/common';

/**
 * Shared decoding rules for society-specific member fields so the member list
 * grid and the member detail page render the same values the same way.
 *
 * - SocietyMemberField10 arrives from i6 as a JSON documents list
 *   (`[{ "title": "...", "file": "..." }]`) built by RPT.f_BuildSocietyMemberField10Json,
 *   even when the society uses the field for plain text such as a contact name (BLS-359).
 * - SocietyMemberField15 holds the BREEDPLAN Herd Completeness of Performance rating
 *   code (`2`, `3h`) which maps to a star logo under /public/breedplan (BLS-375).
 */

/** Item shape for SocietyMemberField10 JSON array. */
export type SocietyMemberField10Item = { title?: string; file?: string };

/** Parse SocietyMemberField10 value as JSON array of { title, file }. Returns null if invalid. */
export function parseSocietyMemberField10(
  value: string | null | undefined,
): SocietyMemberField10Item[] | null {
  if (value == null || value === '') return null;
  const parsed = parseJson(value, null) as unknown;
  if (!Array.isArray(parsed)) return null;
  return parsed.filter(
    (item): item is SocietyMemberField10Item =>
      item != null &&
      typeof item === 'object' &&
      ('title' in item || 'file' in item),
  );
}

/**
 * Plain-text rendering of SocietyMemberField10 for single-line contexts (grid cells,
 * tooltips). Falls back to the raw value when it is not the JSON documents list.
 */
export function getSocietyMemberField10Text(
  value: string | null | undefined,
): string {
  if (value == null || value === '') return '';
  const records = parseSocietyMemberField10(value);
  if (records == null) return value;
  return records
    .map((record) => (record.title ?? record.file ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

/** Base path for breedplan images in public folder (e.g. 0.png, 0_5.png, 1.png, 1_5.png). */
export const BREEDPLAN_IMAGE_BASE = '/breedplan';

/** Breedplan logos in dark mode: invert + hue-rotate restores a natural palette on the dark UI. */
export const BREEDPLAN_IMAGE_DARK_STYLE: CSSProperties = {
  filter: 'invert(1) hue-rotate(180deg)',
};

/**
 * Map a COP rating code to its logo filename (without extension), or '' when blank.
 * Whole stars are digits (`3` -> `3`); ILR2 half stars carry an `h`/`H` suffix
 * (`3h` -> `3_5`). Filenames are digits and underscore only, deliberately letter-free
 * (BLS-375/BLS-360): Azure App Service storage is case-insensitive and keeps the
 * filename case already on disk across deploys, so `3H.png` vs `3h.png` resolved
 * differently per environment while Next serves `public/` by exact name.
 */
export function sanitizeBreedplanFilename(value: string): string {
  const code = value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const match = /^(\d+)(h?)$/.exec(code);
  if (!match) return '';
  const [, stars = '', half] = match;
  return half ? `${stars}_5` : stars;
}

/** Public URL of the COP star logo for a SocietyMemberField15 rating code, or null when blank. */
export function getBreedplanCopImageSrc(
  value: string | number | null | undefined,
): string | null {
  if (value == null || value === '') return null;
  const filename = sanitizeBreedplanFilename(String(value));
  if (filename.length === 0) return null;
  return `${BREEDPLAN_IMAGE_BASE}/${filename}.png`;
}

/**
 * Returns the society member field number for either spelling of the key:
 * the GraphQL / detail-config form (`SocietyMemberField10`, `societyMemberField10`)
 * or the member grid row key produced by mapMemberSettingFieldToRowKey
 * (`SOCIETY_MEMBER_FIELD_10`). Null for any other field.
 */
export function getSocietyMemberFieldNumber(
  field: string | null | undefined,
): number | null {
  if (!field) return null;
  const match = /^SOCIETY_?MEMBER_?FIELD_?(\d+)$/i.exec(field);
  return match ? Number(match[1]) : null;
}
